import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../contexts/ThemeContext';
import authService from '../services/authService';
import './ResetPassword.css';

function NewPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Extract token from URL if not in params (in case it's in query string)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token') || token;
    
    if (!urlToken) {
      setError("Invalid or missing reset token. Please request a new password reset.");
    }
  }, [token]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (formData.newPassword.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (formData.newPassword !== formData.confirmPassword) {
      return "Passwords do not match";
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const validationError = validateForm();
      if (validationError) {
        throw new Error(validationError);
      }

      const urlParams = new URLSearchParams(window.location.search);
      const resetToken = urlParams.get('token') || token;
      
      if (!resetToken) {
        throw new Error("Invalid or missing reset token");
      }

      await authService.resetPassword(resetToken, formData.newPassword);
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Password reset successfully! Please log in with your new password.' 
          }
        });
      }, 2000);

    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message || "Password reset failed. Please try again or request a new reset link.");
    } finally {
      setIsLoading(false);
    }
  };

  // Theme-specific styles
  const themeStyles = useMemo(() => {
    return {
      container: {
        background: isDarkMode 
          ? 'linear-gradient(180deg, #0e1626 0%, #0b1220 100%)'
          : '#eceff4'
      },
      content: {
        background: isDarkMode 
          ? 'linear-gradient(145deg, #151e30 0%, #121a2c 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #f5f9ff 100%)',
        boxShadow: isDarkMode 
          ? '0 6px 12px rgba(0, 0, 0, 0.3)' 
          : '0 6px 12px rgba(0, 0, 0, 0.1)'
      },
      text: {
        primary: isDarkMode ? '#ffffff' : '#2c3e50',
        secondary: isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'
      },
      input: {
        background: 'transparent',
        color: isDarkMode ? '#ffffff' : '#2c3e50',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(44, 62, 80, 0.3)'
      },
      button: {
        background: isDarkMode ? '#0074cc' : '#1976d2',
        color: '#ffffff'
      },
      link: {
        color: isDarkMode ? '#90cdf4' : '#3498db'
      }
    };
  }, [isDarkMode]);

  if (success) {
    return (
      <div className="login-page-container" style={themeStyles.container}>
        <div className="login-container-main" style={themeStyles.content}>
          <div className="success-message" style={{ textAlign: 'center', padding: '40px' }}>
            <h2 style={{ color: themeStyles.text.primary, marginBottom: '20px' }}>
              Password Reset Complete!
            </h2>
            <p style={{ color: themeStyles.text.secondary }}>
              Your password has been successfully changed. Redirecting to login...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page-container" style={themeStyles.container}>
      <div className="login-container-main" style={themeStyles.content}>
        <div className="login-header">
          <FontAwesomeIcon icon={faLock} className="header-icon" style={{ color: themeStyles.text.primary }} />
          <h2 style={{ color: themeStyles.text.primary }}>Set New Password</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="input-group">
            <FontAwesomeIcon 
              icon={faLock} 
              className="input-icon" 
              style={{ color: themeStyles.text.primary }} 
            />
            <input
              type="password"
              name="newPassword"
              placeholder="New Password"
              value={formData.newPassword}
              onChange={handleInputChange}
              required
              disabled={isLoading}
              style={{
                background: themeStyles.input.background,
                color: themeStyles.text.primary,
                borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(44, 62, 80, 0.3)'}`
              }}
            />
          </div>

          <div className="input-group">
            <FontAwesomeIcon 
              icon={faLock} 
              className="input-icon" 
              style={{ color: themeStyles.text.primary }} 
            />
            <input
              type="password"
              name="confirmPassword"
              placeholder="Confirm New Password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              disabled={isLoading}
              style={{
                background: themeStyles.input.background,
                color: themeStyles.text.primary,
                borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(44, 62, 80, 0.3)'}`
              }}
            />
          </div>

          {error && (
            <div className="error-message" style={{ color: '#e53e3e', marginBottom: '15px', textAlign: 'center' }}>
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="password-button"
            disabled={isLoading}
            style={{
              background: isLoading ? '#ccc' : themeStyles.button.background,
              color: themeStyles.button.color,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                Updating password...
              </>
            ) : (
              'Update Password'
            )}
          </button>
        </form>

        <div className="login-link">
          <Link to="/login" style={{ color: themeStyles.link.color }}>Back to Login</Link>
        </div>
      </div>
    </div>
  );
}

export default NewPassword;
